import React, { useState, useEffect } from 'react';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import { ReactComponent as LoginIcon } from '../../../icons/20/solid/arrow-right-on-rectangle.svg';
import { ReactComponent as RegisterIcon } from '../../../icons/20/solid/user-plus.svg';
import { useNavigate } from 'react-router-dom';
import './LoginPage.scss';
import { toast } from 'react-toastify';
import { useAuth } from '../../../AuthContext';
import CIcon from '@coreui/icons-react';
import { cilReload } from '@coreui/icons';
import { useCaptcha } from '../../hooks/useCaptcha';

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaInput, setCaptchaInput] = useState(''); // Состояние для ввода капчи

  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  
  const [organizations, setOrganizations] = useState([]);
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [showOrganizationDropdown, setShowOrganizationDropdown] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [registrationSettings, setRegistrationSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  
  const navigate = useNavigate();
  const { refetch } = useAuth();
  
  // Хук для капчи: теперь возвращает также countdown
  const { loadingCaptcha, captchaUrl, fetchCaptcha, countdown } = useCaptcha();

  // Форматирование времени (MM:SS)
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Загрузка настроек регистрации
  useEffect(() => {
    const fetchRegistrationSettings = async () => {
      setIsLoadingSettings(true);
      try {
        const response = await fetch('/api/registration-settings/');
        
        if (response.ok) {
          const data = await response.json();
          setRegistrationSettings(data);
        } else {
          setRegistrationSettings({
            show_organization_field: true,
            require_email: false,
            auto_approve_users: false,
            default_role: 'viewer',
            organization_naming_template: '{username} - организация'
          });
        }
      } catch (error) {
        console.error('Error fetching registration settings:', error);
        setRegistrationSettings({
          show_organization_field: true,
          require_email: false,
          auto_approve_users: false,
          default_role: 'viewer',
          organization_naming_template: '{username} - организация'
        });
      } finally {
        setIsLoadingSettings(false);
      }
    };

    if (!isLoginMode) {
      fetchRegistrationSettings();
    }
  }, [isLoginMode]);

  // Загрузка списка организаций при переходе в режим регистрации
  useEffect(() => {
    if (!isLoginMode && registrationSettings?.show_organization_field) {
      fetchOrganizations();
    }
  }, [isLoginMode, registrationSettings]);

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/organizations/');
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(organizationSearch.toLowerCase())
  );

  const handleOrganizationSelect = (org) => {
    if (org === 'new') {
      setSelectedOrganization({
        id: null,
        name: organizationSearch,
        isNew: true
      });
      setOrganizationSearch(organizationSearch);
    } else {
      setSelectedOrganization({
        id: org.id,
        name: org.name,
        isNew: false
      });
      setOrganizationSearch(org.name);
    }
    setShowOrganizationDropdown(false);
  };

  const handleOrganizationSearchChange = (value) => {
    setOrganizationSearch(value);
    setSelectedOrganization(null);
    if (value.length > 0) {
      setShowOrganizationDropdown(true);
    } else {
      setShowOrganizationDropdown(false);
    }
  };

  const generateOrganizationName = () => {
    if (!registrationSettings || !registrationSettings.organization_naming_template) {
      return `${username} - организация`;
    }
    
    const template = registrationSettings.organization_naming_template;
    const date = new Date().toLocaleDateString('ru-RU');
    
    return template
      .replace('{username}', username || 'user')
      .replace('{email}', email || '')
      .replace('{date}', date);
  };

  // Обработчик регистрации
  const handleRegister = async (event) => {
    event.preventDefault();
    
    if (!captchaInput) {
        toast.error('Введите код с картинки');
        return;
    }

    setIsLoading(true);

    if (password !== confirmPassword) {
      toast.error('Пароли не совпадают');
      setIsLoading(false);
      return;
    }

    if (registrationSettings?.require_email && !email) {
      toast.error('Email обязателен для регистрации');
      setIsLoading(false);
      return;
    }

    let orgName = '';
    let isExistingOrg = false;

    if (registrationSettings?.show_organization_field) {
      if (!selectedOrganization || !organizationSearch.trim()) {
        toast.error('Выберите или введите название организации');
        setIsLoading(false);
        return;
      }
      orgName = organizationSearch.trim();
      isExistingOrg = !selectedOrganization.isNew;
    } else {
      orgName = generateOrganizationName();
      isExistingOrg = false;
    }

    try {
      const response = await fetch('/api/register/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username, 
          password, 
          confirm_password: confirmPassword,
          email: email || '',
          organization_name: orgName,
          is_existing_organization: isExistingOrg,
          requested_role: registrationSettings?.default_role || 'viewer',
          captcha: captchaInput // Отправляем капчу
        }),
      });

      const data = await response.json();

      if (response.status === 201) {
        if (registrationSettings?.auto_approve_users) {
          toast.success('Регистрация успешна! Вы можете войти в систему.');
          setIsLoginMode(true);
          setPassword('');
          setConfirmPassword('');
        } else {
          toast.success('Заявка на регистрацию отправлена! Ожидайте подтверждения администратора.');
          setUsername('');
          setPassword('');
          setConfirmPassword('');
          setEmail('');
          setOrganizationSearch('');
          setSelectedOrganization(null);
        }
        // Сброс капчи после успеха
        setCaptchaInput('');
        fetchCaptcha();
      } else {
        toast.error(data.error || 'Ошибка при регистрации');
        
        // Если ошибка связана с капчей, обновляем её
        if (data.error === 'Неверный код с картинки' || data.error === 'Капча не введена или истекла') {
             fetchCaptcha();
             setCaptchaInput('');
        }
        
        if (data.details) {
          Object.values(data.details).forEach(error => {
            toast.error(Array.isArray(error) ? error[0] : error);
          });
        }
      }
    } catch (error) {
      toast.error('Произошла ошибка. Попробуйте позже.');
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик входа
  const handleLogin = async (event) => {
    event.preventDefault();
    
    if (!captchaInput) {
        toast.error('Введите код с картинки');
        return;
    }
    
    setIsLoading(true);

    try {
      const response = await fetch('/api/token/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
            username, 
            password,
            captcha: captchaInput // Отправляем капчу
        }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.access);
        
        try {
          const statusResponse = await fetch('/api/registration-status/', {
            headers: {
              'Authorization': `Bearer ${data.access}`,
            },
          });
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            
            if (statusData.is_approved === false) {
              localStorage.removeItem('token');
              toast.info('Ваша заявка находится на рассмотрении. Ожидайте подтверждения администратора.');
              setIsLoading(false);
              return;
            }
          }
        } catch (statusError) {
          console.log('Registration status endpoint not available, proceeding...');
        }

        if (window.Android && typeof window.Android.saveAuthToken === 'function') {
          window.Android.saveAuthToken(data.access);
        }

        await refetch();
      } else {
        if (data.detail) {
          toast.error(data.detail);
        } else {
          toast.error('Ошибка авторизации');
        }
        // Обновляем капчу при ошибке
        fetchCaptcha();
        setCaptchaInput('');
      }
    } catch (error) {
      toast.error('Сетевая ошибка. Проверьте подключение.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isLoginMode) {
      handleLogin(event);
    } else {
      handleRegister(event);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setPassword('');
    setConfirmPassword('');
    
    // Обновляем капчу при переключении режима
    fetchCaptcha();
    setCaptchaInput('');

    if (!isLoginMode) {
      setUsername('');
    }
    setEmail('');
    setOrganizationSearch('');
    setSelectedOrganization(null);
    setShowOrganizationDropdown(false);
  };

  if (isLoadingSettings && !isLoginMode) {
    return (
      <div className="login_div">
        <h1>Загрузка настроек...</h1>
      </div>
    );
  }
  
  return (
    <div className="login_div">
      <h1>{isLoginMode ? 'ВХОД' : 'РЕГИСТРАЦИЯ'}</h1>
      <form onSubmit={handleSubmit}>
        {!isLoginMode && registrationSettings && (
          <>
            {/* Поле организации - показываем только если включено в настройках */}
            {registrationSettings.show_organization_field && (
              <div className="organization-field">
                <label className="organization-label">Организация</label>
                <div className="organization-input-container">
                  <Input
                    type="text"
                    placeholder="Название организации"
                    value={organizationSearch}
                    onChange={(e) => handleOrganizationSearchChange(e.target.value)}
                    onFocus={() => setShowOrganizationDropdown(true)}
                    disabled={isLoading}
                    className="organization-input"
                  />
                  
                  {showOrganizationDropdown && (
                    <div className="organization-dropdown">
                      {filteredOrganizations.length > 0 ? (
                        <>
                          {filteredOrganizations.map(org => (
                            <div
                              key={org.id}
                              className="organization-option"
                              onClick={() => handleOrganizationSelect(org)}
                            >
                              <span className="organization-name">{org.name}</span>
                            </div>
                          ))}
                        </>
                      ) : organizationSearch.trim() && (
                        <div
                          className="organization-option new-organization"
                          onClick={() => handleOrganizationSelect('new')}
                        >
                          <span className="organization-name">Создать новую организацию:</span>
                          <span className="new-organization-name">"{organizationSearch}"</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedOrganization && (
                  <div className="selected-organization-info">
                    {selectedOrganization.isNew ? (
                      <span>Будет создана новая организация: <strong>{selectedOrganization.name}</strong></span>
                    ) : (
                      <span>Выбрана организация: <strong>{selectedOrganization.name}</strong></span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Email (проверяем обязательность по настройкам) */}
            <div>
              <Input
                type="email"
                placeholder={registrationSettings.require_email ? "Email (обязательно)" : "Email (необязательно)"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required={registrationSettings.require_email}
              />
            </div>
          </>
        )}

        {/* Общие поля */}
        <div>
          <Input
            type="text"
            placeholder="Имя пользователя"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>
        <div>
          <Input
            type="password"
            placeholder="Пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>
        {!isLoginMode && (
          <div>
            <Input
              type="password"
              placeholder="Подтвердите пароль"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>
        )}
        
        {/* --- БЛОК КАПЧИ --- */}
        <div className="captcha-block">
            <div className="captcha-header">
                <span className="captcha-label">Код с картинки</span>
                {/* Таймер */}
                <span className={`captcha-timer ${countdown < 30 ? 'text-danger' : ''}`}>
                    Обновится через: {formatTime(countdown)}
                </span>
            </div>
            
            <div className="captcha-image-row">
                <div className="captcha-wrapper">
                    {loadingCaptcha ? (
                        <div className="captcha-loading">Загрузка...</div>
                    ) : (
                        captchaUrl && <img src={captchaUrl} alt="Captcha" className="captcha-img" />
                    )}
                </div>
                <button 
                    type="button" 
                    className="captcha-refresh-btn" 
                    onClick={() => fetchCaptcha(false)} // false = ручное обновление
                    disabled={loadingCaptcha || isLoading} // Блокируем при загрузке
                    title="Обновить картинку"
                >
                    <CIcon icon={cilReload} size="lg"/>
                </button>
            </div>
            <Input
                type="text"
                placeholder="Введите символы"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                disabled={isLoading}
                required
                autoComplete="off"
            />
        </div>
        {/* --- КОНЕЦ БЛОКА КАПЧИ --- */}
        
        <Button 
          icon={isLoginMode ? LoginIcon : RegisterIcon} 
          type="submit"
          disabled={isLoading || loadingCaptcha} // Нельзя отправить, пока грузится капча
        >
          {isLoading ? 'Загрузка...' : (isLoginMode ? 'Войти' : 'Зарегистрироваться')}
        </Button>
        {!isLoginMode && !registrationSettings?.auto_approve_users && (
          <div className="registration-info">
            <p>Ваша заявка будет рассмотрена администратором в течение 24 часов.<br/>Уведомление о подтверждении придет на указанный Email.</p>
          </div>
        )}
      </form>
      
      <div className="toggle-mode">
        <button type="button" className="toggle-button" onClick={toggleMode}>
          {isLoginMode ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
        </button>
      </div>

      <div className="support-link">
        <a href="https://t.me/AppStructuraSupport" target="_blank" rel="noopener noreferrer">
          Написать в поддержку
        </a>
      </div>
    </div>
  );
}

export default LoginPage;